import React, { Fragment, HTMLProps, ReactNode, useEffect, useState } from 'react'
import { Collapse, NavLink } from 'react-bootstrap'
import { LinkContainer } from 'react-router-bootstrap'
import { matchPath, useLocation } from 'react-router-dom'

import { NavRoute } from '../../AppRouter'
import { ReactComponent as CaretDownIcon } from '../../assets/images/chevron.svg'

import styles from './sidebar.module.scss'

export interface SidebarProps {
  header: ReactNode
  routes: NavRoute[]
  footer: ReactNode
}

interface ItemProps extends Pick<HTMLProps<HTMLSpanElement>, 'onClick'> {
  route: NavRoute
  isActive?: boolean
  type?: 'item' | 'subItem'
  expanded?: boolean
}

interface SidebarMenuItemProps {
  route: NavRoute
}

const Item = ({ route, isActive = false, type = 'item', expanded, ...props }: ItemProps) => {
  return (
    <span className={[styles.navItem, styles[type], isActive && styles.active].join(' ')} {...props}>
      <span className={styles.icon}>{route.icon}</span>
      <span className={styles.content}>{route.title}</span>
      {expanded !== undefined && <CaretDownIcon className={`${styles.caret} ${!expanded ? styles.collapsed : ''}`} />}
    </span>
  )
}

interface LinkProps {
  path: string
  children: ReactNode
}

const Link = ({ path, children }: LinkProps) => {
  return (
    <LinkContainer to={path!}>
      <NavLink className={styles.link}>{children}</NavLink>
    </LinkContainer>
  )
}

const MenuItem = ({ route }: SidebarMenuItemProps) => {
  const childRoutes =
    route.children?.map((childRoute) => ({
      ...childRoute,
      ...(childRoute.path && { path: [route.path, childRoute.path].join('/') }),
    })) || []

  const location = useLocation()
  const sidebarLocation = location.pathname.split('/').slice(0, 3).join('/')

  const activeRoute = childRoutes.filter((childRoute) => matchPath(childRoute.path!, sidebarLocation))[0]

  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!!activeRoute) {
      setOpen(true)
    }
  }, [activeRoute])

  return (
    <div>
      <Item route={route} isActive={!!activeRoute} expanded={open} onClick={() => setOpen((open) => !open)} />
      <Collapse in={open}>
        <div className={styles.subMenu}>
          {childRoutes.map((childRoute, index) => (
            <Link key={index} path={childRoute.path!}>
              <Item route={childRoute} type={'subItem'} isActive={activeRoute?.path === childRoute.path} />
            </Link>
          ))}
        </div>
      </Collapse>
    </div>
  )
}

export const Sidebar = ({ header, routes, footer }: SidebarProps) => {
  const location = useLocation()
  const sidebarLocation = location.pathname.split('/').slice(0, 3).join('/')

  return (
    <aside className={styles.sidebar}>
      <div>{header}</div>
      <nav className={styles.nav}>
        {routes.map((route, index) => (
          <Fragment key={index}>
            {route.children ? (
              <MenuItem route={route} />
            ) : (
              <Link path={route.path!}>
                <Item route={route} isActive={!!matchPath(route.path!, sidebarLocation)} />
              </Link>
            )}
          </Fragment>
        ))}
      </nav>
      <div>{footer}</div>
    </aside>
  )
}
